import type { ApiClient } from '../../lib/api/client';
import type { PackSite } from '../../lib/api/types';
import type { DraftRecord, OfflineStorage } from '../../lib/offline/types';
import { resolveTemplateForSite } from '../../lib/sync/pack';
import type { DraftService } from '../capture/draft-service';

export type StartResult = { ok: true; draft: DraftRecord; resumed: boolean } | { ok: false; reason: 'NO_TEMPLATE' };

/**
 * Start (or resume) a survey for a site. The template version is resolved
 * from the offline pack (or online, then cached) and the draft is pinned to
 * it for its whole life (ADR-0009). The server claims the draft lock for this
 * device on the first synced snapshot (ADR-0006).
 */
export async function startSurvey(
  deps: { api: ApiClient; storage: OfflineStorage; drafts: DraftService },
  site: PackSite,
  surveyType: string,
  userId: string,
): Promise<StartResult> {
  const existing = (await deps.drafts.list()).find(
    (d) => d.siteId === site.id && d.surveyType === surveyType && d.reporterUserId === userId && (d.status === 'DRAFT' || d.status === 'RETURNED'),
  );
  if (existing) return { ok: true, draft: existing, resumed: true };
  const tpl = await resolveTemplateForSite(deps.api, deps.storage, site.id, surveyType);
  if (!tpl) return { ok: false, reason: 'NO_TEMPLATE' };
  if (!(await deps.storage.getSite(site.id))) await deps.storage.putSites([site]);
  // With the template, the customer/site/branch fields are filled from the site at once (C1).
  const draft = await deps.drafts.create({ site, surveyType, templateVersionId: tpl.versionId, reporterUserId: userId, template: tpl.definition });
  return { ok: true, draft, resumed: false };
}
